import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useFlowRuntime } from '../FlowRuntimeContext';
import type { FinixTippingComponentConfig, FlowComponentRenderProps, TippingComponentConfig } from '../types';
import { formatCurrency, getFinixBreakdown, parseTipAmount } from './finixTipping/finixTipFee';
import { getPublicFinixConfig, loadPublicFinix, payPublicFinix } from '../../services/cxPublicFinixService';
import LegacyFinixPreview from './FinixTippingBlock';
import ApruvTermsNotice from '../../components/common/ApruvTermsNotice';
import TippingAmountSelector from '../presentation/FeedbackTipAmountSelector';
import TippingFeeSummary from '../presentation/FeedbackTipFeeSummary';
import { createTipPaymentRecovery, type TipPaymentRecovery } from '../presentation/FeedbackTippingCard';

/** Public Finix uses hosted tokenization and the invite-bound API exclusively. */
type PublicFinixTippingProps = FlowComponentRenderProps<FinixTippingComponentConfig> & {
  paymentRecovery?: TipPaymentRecovery;
};

export default function FinixTippingEntry(props: PublicFinixTippingProps) {
  const runtime = useFlowRuntime();
  return runtime.mode === 'preview' ? <LegacyFinixPreview {...props}/> : <PublicFinixTippingBlock {...props}/>;
}

function PublicFinixTippingBlock({config, paymentRecovery, setValue, onAction, goNext}: PublicFinixTippingProps) {
  const runtime = useFlowRuntime();
  const allowed = runtime.mode === 'live' && runtime.paymentsEnabled === true && !!runtime.surveyInviteCode;
  const [presetAmount, setPresetAmount] = useState<number | undefined>(config.amounts[0]?.value);
  const [customAmount, setCustomAmount] = useState('');
  const [coverFee, setCoverFee] = useState(true);
  const [checkout, setCheckout] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<'idle'|'submitting'|'succeeded'|'pending'|'failed'>('idle');
  const [error, setError] = useState('');
  const mount = useRef<HTMLDivElement>(null);
  const submitting = useRef(false);
  const idempotency = useRef<string | null>(null);
  const callbacks = useRef({setValue,onAction});
  callbacks.current = {setValue,onAction};
  const localRecovery = useRef<TipPaymentRecovery>(createTipPaymentRecovery());
  const recovery = paymentRecovery ?? localRecovery.current;
  const hasCustomAmount = customAmount.trim().length > 0;
  const parsedCustomAmount = parseTipAmount(customAmount);
  const amount = hasCustomAmount ? (parsedCustomAmount ?? 0) : (presetAmount ?? 0);
  const breakdown = getFinixBreakdown(amount, coverFee);
  const amountCents = Math.round(breakdown.total * 100);
  const minimum = Math.max(1,config.minimumAmount ?? 1);
  const maximum = Math.min(100,config.maximumAmount ?? 100);
  const valid = Number.isFinite(amount) && amount >= minimum && amount <= maximum && Math.abs(Math.round(amount*100) - amount*100) < 0.00001;
  const stripeLayoutConfig: TippingComponentConfig = {
    ...config,
    checkoutEnabled: true,
    title: config.tipAmountLabel,
    type: 'tipping',
  };
  const updateAmount = (next: {amount?: number; customAmount?: string}) => {
    setPresetAmount(next.amount);
    setCustomAmount(next.customAmount ?? '');
  };

  useEffect(() => {
    if (!checkout || !allowed || !mount.current) return;
    let cancelled = false;
    let destroy: (() => void) | undefined;
    const element = mount.current;
    const code = runtime.surveyInviteCode!;
    Promise.all([getPublicFinixConfig(code), loadPublicFinix()]).then(([payment, sdk]) => {
      if (cancelled) return;
      const auth = sdk.Auth(payment.environment === 'prod' ? 'live' : 'sandbox', payment.merchantId);
      const form = sdk.PaymentForm(element, payment.environment, payment.applicationId, {
        paymentMethods: ['card'],
        showAddress: true,
        hideFields: ['name','address_line1','address_line2','address_city','address_region','address_state','address_country'],
        requiredFields: ['address_postal_code'],
        onSubmit: async (tokenError, response) => {
          if (cancelled || submitting.current) return;
          const token = response?.data?.id;
          const sessionKey = auth.getSessionKey();
          if (tokenError || !token || !sessionKey) {setError('Unable to prepare payment. Please check your details.');return;}
          submitting.current = true;
          setState('submitting'); setError('');
          idempotency.current ??= crypto.randomUUID();
          try {
            const payload = {token, sessionKey, selectedAmountCents:Math.round(amount*100), coverProcessingFee:coverFee, componentId:config.id, idempotencyKey:idempotency.current};
            let result: Awaited<ReturnType<typeof payPublicFinix>> | undefined;
            for (let attempt = 0; attempt < 10; attempt += 1) {
              try {
                result = await payPublicFinix(code, payload);
                if (result.status === 'succeeded') break;
              } catch (failure) {
                const failureCode = axios.isAxiosError(failure) ? failure.response?.data?.error?.code : undefined;
                if (failureCode !== 'TIP_ALREADY_SUBMITTED' || attempt === 9) throw failure;
              }
              if (cancelled) return;
              await new Promise(resolve => window.setTimeout(resolve, 1000));
            }
            if (cancelled) return;
            if (!result || result.status !== 'succeeded') {
              setState('pending');
              setError('We could not confirm the payment yet. Please do not submit it again.');
              return;
            }
            setState(result.status);
            const value = {amount, total:breakdown.total, processingFee:breakdown.processingFee, technicianTip:breakdown.technicianTip, coverProcessingFee:coverFee, status:'submitted' as const, paymentGateway:'finix' as const};
            recovery.confirmedValue = value;
            callbacks.current.setValue(config.id,value);
            callbacks.current.onAction?.({type:'tip',componentId:config.id,value:amount,metadata:{paymentGateway:'finix',values:{[config.id]:value}}});
          } catch (failure) {
            const failureCode = axios.isAxiosError(failure) ? failure.response?.data?.error?.code : undefined;
            if (!cancelled && ['VALIDATION_ERROR', 'TIP_POLICY_REJECTED'].includes(failureCode)) {
              // These server checks run before any reservation or provider request.
              submitting.current = false;
              setState('idle'); setCheckout(false);
              setError('This tip could not be submitted. Please check the amount and try again.');
              return;
            }
            if (!cancelled && failureCode === 'TIP_FAILED') {
              setState('failed'); setError('Your payment was declined. Please contact support before trying again.');
              return;
            }
            // A timeout can happen after the charge. Never invent a new key/retry.
            if (!cancelled) {setState('pending');setError('We could not confirm the payment yet. Please do not submit it again.');}
          }
        },
      });
      destroy = () => form.destroy?.();
    }).catch(() => {if (!cancelled) {setError('Tipping is unavailable right now. You can still leave your feedback.');setCheckout(false);}});
    return () => {cancelled = true;destroy?.();element.replaceChildren();};
  // The selected amount is locked while the hosted form is mounted.
  }, [checkout, allowed, runtime.surveyInviteCode, amountCents, amount, config.id, coverFee]);

  if (state === 'succeeded') {
    const value = {amount, status:'submitted', paymentGateway:'finix'};
    return <Stack spacing={2} sx={{color:'var(--cx-brand-text)',textAlign:'center',py:1}}>
      <Alert severity="success" sx={{textAlign:'left'}}>
        <Typography variant="subtitle1" sx={{fontWeight:700}}>Tip sent successfully</Typography>
        <Typography variant="body2">Your card was charged {formatCurrency(breakdown.total)}. Your technician receives {formatCurrency(breakdown.technicianTip)}.</Typography>
      </Alert>
      <Button variant="contained" sx={{backgroundColor:'var(--cx-brand-primary)',color:'var(--cx-brand-primary-contrast)',textTransform:'none'}} onClick={()=>{if(onAction)onAction({type:'tip',componentId:config.id,value:amount,metadata:{paymentGateway:'finix',values:{[config.id]:value}}});else goNext();}}>Continue</Button>
    </Stack>;
  }
  if (state === 'failed') return <Alert severity="error">{error}</Alert>;
  if (state === 'pending') return <Alert severity="info">{error || 'Your payment is processing. Please do not submit it again.'}</Alert>;
  if (dismissed) return null;
  return <Stack spacing={2} sx={{color:'var(--cx-brand-text)', '& .MuiButton-root':{textTransform:'none'}}}>
    {!allowed && <Alert severity="info">Payments are disabled in this preview.</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    {!checkout && <Stack spacing={0.75}>
      <fieldset style={{border:0,margin:0,padding:0,minWidth:0}}>
        <Stack spacing={2} useFlexGap>
          {config.tipAmountLabel && <Typography sx={{fontSize:16,fontWeight:600,lineHeight:'24px',textAlign:'center'}}>{config.tipAmountLabel}</Typography>}
          <TippingAmountSelector amount={valid?amount:undefined} amountLabel={formatCurrency(amount)} config={stripeLayoutConfig} customAmount={customAmount} customAmountBelowMinimum={hasCustomAmount&&!valid} onChange={updateAmount}/>
          {config.termsEnabled !== false && (
            <Box sx={{'&& .MuiTypography-caption':{fontSize:12,lineHeight:'18px',color:'var(--cx-brand-text-muted)'},'&& .MuiTypography-caption button':{fontWeight:600}}}>
              <ApruvTermsNotice linkLabel={config.termsLinkLabel} modalTitle={config.termsModalTitle} termsHtml={config.termsHtml} text={config.termsText} textAlign="left" linkDecoration="none" trailingPunctuation="" linkColor="var(--cx-brand-text, #111827)"/>
            </Box>
          )}
          <TippingFeeSummary amountLabel={valid?formatCurrency(breakdown.amount):'—'} coverProcessingFee={coverFee} onCoverProcessingFeeChange={setCoverFee} processingFeeLabel={valid?formatCurrency(breakdown.processingFee):'—'} technicianTipLabel={valid?formatCurrency(breakdown.technicianTip):'—'} totalLabel={valid?formatCurrency(breakdown.total):`Choose ${formatCurrency(minimum)}–${formatCurrency(maximum)}`}/>
          <Button disableElevation fullWidth disabled={!allowed || !valid} onClick={()=>setCheckout(true)} variant="contained" sx={{minHeight:48,fontSize:15,fontWeight:600,textTransform:'none',bgcolor:'var(--cx-brand-button-selected-bg)',color:'var(--cx-brand-button-selected-text)','&:hover':{bgcolor:'var(--cx-brand-button-selected-bg)'}}}>Continue</Button>
        </Stack>
      </fieldset>
      <Button color="inherit" fullWidth disabled={state==='submitting'} onClick={()=>{setDismissed(true);if(onAction)onAction({type:"continue",componentId:config.id,value:false});else goNext();}} variant="text" sx={{minHeight:48,textTransform:'none',fontSize:15,fontWeight:600,color:'var(--cx-brand-text-muted)'}}>Skip tipping</Button>
    </Stack>}
    {checkout && <Box>
      <Typography variant="body2">Pay {formatCurrency(breakdown.total)} by credit or debit card</Typography>
      <Box sx={{display:state==='submitting'?'none':'block'}} ref={mount}/>
      {state==='submitting' && <Box role="status" aria-label="Processing payment" sx={{alignItems:'center',display:'flex',justifyContent:'center',minHeight:240}}><CircularProgress size={32}/></Box>}
    </Box>}
    {checkout && <Button disabled={state==='submitting'} onClick={()=>setCheckout(false)} variant="text">Change tip</Button>}
  </Stack>;
}
